// The one way a client's status changes after the client exists.
//
// client_change_status() in the database checks the status against the payer,
// saves it and writes the Stage Transition log; the database then derives the
// zone and, if the client has just closed, pauses or ends their schedules,
// cancels their future visits, ends caregiver assignments and withdraws open
// agency offers (clients_on_close). What only the app can do happens here:
// telling AxisCare (and creating the client there the first time they go
// Active), and the one family email a stage change sends.
//
// Every screen that changes a status goes through StageTransitionModal, which
// calls this. Do not write clients.current_status directly anywhere else.
import { supabase, updateClient, addActivityLog } from './supabase';
import { updateAxisCareClient } from './axiscare';
import { pushClientToAxisCare } from './axiscareClient';
import { notifyDma80Submitted } from './notifications';
import { RECEIVING_SERVICE } from './serviceModel';
import { SUPPORT_EMAIL } from './org';

export const DMA80 = 'Submitted (DMA80)';
export const ACTIVE = RECEIVING_SERVICE;

/**
 * Returns { data, error, warnings, axiscare }. `error` means nothing changed.
 * `warnings` are follow-ups that failed after the status was saved (AxisCare,
 * the family email); the status change itself stands. `axiscare` is the result
 * of creating the client in AxisCare when this move did that, else null.
 */
export const changeClientStatus = async ({ client, newStatus, notes, profile, familyEmailHandled = false }) => {
  const { data, error } = await supabase.rpc('client_change_status', {
    p_client_id: client.id, p_new_status: newStatus, p_notes: notes,
  });
  if (error) return { error, warnings: [] };
  const warnings = [];
  let axiscare = null;

  // CAERA is authoritative for status: a discharged or suspended client must
  // not stay schedulable in AxisCare. Best-effort; the CAERA save stands.
  if (client.axiscare_client_id) {
    const axisStatus = newStatus === ACTIVE ? 'Active' : 'Inactive';
    const { data: pushData, error: pushErr } = await updateAxisCareClient(client.axiscare_client_id, { status: axisStatus });
    if (pushErr || pushData?.errors?.length) {
      warnings.push(`Status saved in CAERA, but AxisCare could not be updated: ${pushErr?.message || pushData.errors[0]}`);
    }
  } else if (newStatus === ACTIVE) {
    // First time Active and not yet in AxisCare: create them there, so they
    // can be scheduled without anyone retyping the record. A same-name client
    // already in AxisCare is linked only when the date of birth agrees too;
    // otherwise it is left for a person on the profile's Create in AxisCare
    // button, which asks.
    try {
      axiscare = await pushClientToAxisCare(client, { profile });
      if (axiscare.outcome === 'already_linked') {
        await updateAxisCareClient(axiscare.axisId, { status: 'Active' });
        axiscare = null;
      } else if (axiscare.outcome === 'needs_review') {
        warnings.push(`Not created in AxisCare: a client named ${client.first_name} ${client.last_name} is already there (ID ${axiscare.axisId}) with a different or missing date of birth. Use Create in AxisCare on the profile to link it if it is the same child.`);
      }
    } catch (e) {
      warnings.push(`Status saved in CAERA, but ${client.first_name} could not be created in AxisCare: ${e.message}. Use Create in AxisCare on the profile to try again.`);
    }
  }

  // Stage changes are otherwise reported in the daily digest. The family is
  // told once when their packet reaches DCH; dma80_parent_notified_at is the
  // guard, so moving out of DMA-80 and back does not email them twice. A move
  // offered by the GAPP Submissions card skips this: the card has already
  // emailed the family, once for that submission and in its own wording.
  if (newStatus === DMA80 && !familyEmailHandled && !client.dma80_parent_notified_at) {
    const { error: mailErr } = await notifyDma80Submitted(client);
    if (mailErr) {
      warnings.push(`Status saved, but the family could not be emailed: ${mailErr.message}. Send it from the client's Activity Log once the address is corrected.`);
    } else {
      await updateClient(client.id, { dma80_parent_notified_at: new Date().toISOString() });
      await addActivityLog({
        client_id: client.id, client_ref: client.client_id,
        activity_type: 'Client Documentation Followup',
        notes: `Family emailed that the DMA-80 packet was submitted to DCH — sent to ${client.guardian_email}, copied to ${SUPPORT_EMAIL}.`,
        performed_by_name: profile.full_name, performed_by_id: profile.id,
      });
    }
  }

  return { data, error: null, warnings, axiscare };
};
